import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { assetsAssetV2List, assetsHybridSearchCreate } from '@goldcast/api/content';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { getClipDurationInSeconds } from '@goldcast/player-config/src/utils/clipUtils';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import Button from '@/components/atoms/Button/Button';
import { formatTime } from '@/libs/core';
import { currentUser } from '@/stores/user';
import { showErrorToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';
import TooltipV2 from '@/components/TooltipV2';
import { classnames } from '@/libs/utils';
import IconButton from '@/components/atoms/Button/IconButton';
import HomePageClipThumbnail from '@/Pages/ClipsListPage/HomePageClipThumbnail';
import Icon from '@/components/atoms/Icon';

interface TopicEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTopic: string;
  onSave: (newTopic: string, newSearchTerm: string) => void;
  clips: Clip[];
  isLoading?: boolean;
  currentSearchTerm: string;
}

const TopicEditModal: React.FC<TopicEditModalProps> = ({
  isOpen,
  onClose,
  currentTopic,
  onSave,
  clips,
  isLoading = false,
  currentSearchTerm
}) => {
  const [searchTerm, setSearchTerm] = useState(currentSearchTerm);
  const [topicTitle, setTopicTitle] = useState(currentTopic);
  const [selectedClips, setSelectedClips] = useState<Clip[]>(clips || []);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { logger } = useAppContext();

  const resetState = useCallback(() => {
    setSearchTerm(currentSearchTerm);
    setTopicTitle(currentTopic);
    setSelectedClips(clips || []);
    setIsSearchLoading(false);
    setIsSaveDisabled(true);
  }, [currentSearchTerm, currentTopic, clips]);

  const resetStateRef = useRef(resetState);
  resetStateRef.current = resetState;

  useEffect(() => {
    if (isOpen) {
      resetStateRef.current();
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSave = () => {
    if (searchTerm.trim() && searchTerm.trim() !== currentSearchTerm) {
      onSave(topicTitle, searchTerm);
      setIsSaveDisabled(true);
      return;
    }
    handleClose();
  };

  const createSearchQuery = async (searchQuery: string, signal: AbortSignal) => {
    const organizationId = currentUser.getSnapshot()?.organization;
    if (!organizationId) {
      return;
    }

    return assetsHybridSearchCreate({
      signal,
      body: {
        organization_id: organizationId,
        search_term: searchQuery,
        title: searchQuery,
        generation_type: 'CLIP',
        selected_sources: [],
        search_type: 'CL_HOMEPAGE'
      }
    });
  };

  const fetchClipsForSearch = async (savedSearchId: string, signal: AbortSignal) => {
    return assetsAssetV2List({
      signal,
      queryParams: {
        saved_search: savedSearchId,
        types: 'CLIP',
        limit: 6,
        ordering: '-created_at'
      }
    });
  };

  const processSearchResults = (clips: Clip[] | undefined, searchQuery: string) => {
    if (!clips || clips.length === 0) {
      return {
        clips: [],
        topicTitle: searchQuery,
        searchTerm: searchQuery
      };
    }

    const firstClip = clips[0];
    const savedSearch = firstClip?.saved_search;

    return {
      clips: clips.filter(clip => clip && clip.id),
      topicTitle: savedSearch?.title || searchQuery,
      searchTerm: savedSearch?.search_term || searchQuery
    };
  };

  const handleSearch = useCallback(async () => {
    const trimmedSearchTerm = searchTerm.trim();

    if (!trimmedSearchTerm || isSearchLoading) {
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsSearchLoading(true);
    setIsSaveDisabled(false);

    try {
      const savedSearch = await createSearchQuery(trimmedSearchTerm, controller.signal);

      if (!savedSearch?.id) {
        showErrorToast('Failed to create search. Please try again.');
        return;
      }

      const response = await fetchClipsForSearch(savedSearch.id, controller.signal);

      if (!response?.results) {
        showErrorToast('No results found. Please try a different search term.');
        return;
      }

      const {
        clips,
        topicTitle: newTopicTitle,
        searchTerm: newSearchTerm
      } = processSearchResults(response.results as unknown as Clip[], trimmedSearchTerm);

      setSelectedClips(clips);
      setTopicTitle(newTopicTitle);
      setSearchTerm(newSearchTerm);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      logger.error('Search failed:', error);
      showErrorToast('Error while searching for clips. Please try again.');
    } finally {
      if (abortControllerRef.current === controller) {
        setIsSearchLoading(false);
      }
    }
  }, [searchTerm, isSearchLoading, logger]);

  const isSearchButtonDisabled = useMemo(() => {
    return isLoading || !searchTerm.trim() || searchTerm.trim() === currentSearchTerm || isSearchLoading;
  }, [isLoading, searchTerm, currentSearchTerm, isSearchLoading]);

  return (
    <ContentDialog
      isOpen={isOpen}
      setIsOpen={handleClose}
      title=""
      size="small"
      panelClassNames="!max-w-[1040px] relative"
    >
      <div className="p-5">
        <div className="absolute top-0 z-50 flex items-center justify-between pt-5">
          <div className="font-medium text-slate-950">Edit Theme</div>
        </div>
        <div className="flex flex-col">
          <div>
            <p className="mb-4 text-sm text-slate-800">
              Edit or add a new theme and preview the updates before saving them to your Homepage. When saved, the
              current carousel will be replaced with the updated title and search results.
            </p>
            <div className="mb-4">
              <div className="mb-2 flex items-center gap-1">
                <label htmlFor="topic-input" className="text-sm text-slate-950">
                  New Theme
                </label>
                <TooltipV2
                  tooltipId="new-theme-tooltip"
                  tooltipElement={<Icon name="IconInfoCircle" size={16} className="text-slate-950" />}
                  tooltipContent="AI summarizes the themes for the homepage but we recommend adding a descriptive theme for better results"
                  size="xsmall"
                  colorTheme="dark"
                  place="right"
                  className="z-50 !rounded-md bg-slate-950 px-3 py-2 !text-[10px] font-normal !leading-[15px] text-white"
                />
              </div>
              <div className="relative">
                <textarea
                  id="topic-input"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="min-h-[80px] w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-slate-950 placeholder:text-slate-400 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
                  placeholder="Enter a search query"
                  autoFocus
                  rows={3}
                />
                <IconButton
                  icon={isSearchLoading ? 'IconLoader2' : 'IconSend'}
                  size="base"
                  type="button"
                  onClick={handleSearch}
                  variation="text"
                  trackingId="edit-topic-search-button"
                  disabled={isSearchButtonDisabled}
                  buttonClassName={classnames(
                    'absolute bottom-4 right-2 size-8 rounded-lg',
                    !isSearchButtonDisabled ? 'bg-slate-900' : 'bg-slate-200',
                    {
                      'bg-slate-100': isLoading || isSearchLoading
                    }
                  )}
                  iconClassName={classnames(isSearchButtonDisabled ? 'text-slate-400' : 'text-white', {
                    'animate-spin': isLoading || isSearchLoading
                  })}
                />
              </div>
            </div>
          </div>
          <div>
            <div className="h-[284px] rounded-xl border border-slate-200 bg-white p-3 pr-0">
              <div className="mb-4 flex items-center gap-1">
                <p className="text-xs text-slate-950">Preview</p>
                <TooltipV2
                  tooltipId="preview-tooltip"
                  tooltipElement={<Icon name="IconInfoCircle" size={16} className="text-slate-950" />}
                  tooltipContent="Before saving the theme to homepage you can preview the results by entering the theme"
                  size="xsmall"
                  colorTheme="dark"
                  place="right"
                  className="z-50 !rounded-md !px-3 !py-2 !text-[10px] font-normal !leading-[15px] text-white"
                />
              </div>
              {!isSearchLoading ? (
                <>
                  <p className="mb-3 text-sm text-slate-950">{topicTitle}</p>
                  <div className="no-scrollbar flex gap-4 overflow-x-auto">
                    {selectedClips.length > 0 ? (
                      selectedClips.map((clip, index) => {
                        if (!clip || !clip.id) {
                          return null;
                        }
                        return (
                          <div
                            key={clip.id}
                            className={`h-[196px] shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-[250ms] ${
                              index === selectedClips.length - 1 ? 'mr-3' : ''
                            }`}
                            style={{ maxWidth: '250px' }}
                          >
                            <div className="relative h-[140px] cursor-pointer">
                              <div className="group flex aspect-video h-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                                <HomePageClipThumbnail
                                  clip={clip}
                                  isClipActive={false}
                                  isClipUnseen={false}
                                  layout="fullWidth"
                                />
                              </div>
                              {clip?.asset_metadata && (
                                <div className="absolute bottom-2 right-2 rounded bg-slate-950 px-2 py-1 text-2xs text-white">
                                  {formatTime(getClipDurationInSeconds(clip as Clip))}
                                </div>
                              )}
                            </div>

                            <div className="p-2">
                              <div className="mb-[4px] truncate text-sm font-medium text-slate-950">
                                {clip?.title || 'Untitled Clip'}
                              </div>
                              <p className="flex cursor-pointer items-center gap-1 text-slate-500">
                                <Icon name="IconVideo" size={12} className="shrink-0" />
                                <span className="truncate text-xs">{clip?.content?.title || 'Untitled Content'}</span>
                              </p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex h-[196px] w-full items-center justify-center text-sm text-slate-500">
                        No clips found
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-[84px] flex justify-center pr-2">
                  <div className="flex w-[450px] flex-col items-center justify-center gap-2 text-center">
                    <Icon name="IconSparkles" size={20} className="text-slate-500" />
                    <p className="text-slate-500">
                      Analyzing your library to create clips. This may take few minutes, you can still go ahead and save
                      the theme
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3 border-t border-slate-200 p-5">
        <Button
          variation="outline"
          buttonSize="base"
          onClick={handleClose}
          disabled={isLoading}
          trackingId="cancel-topic-edit"
        >
          Cancel
        </Button>
        <Button
          variation="filled"
          buttonSize="base"
          onClick={handleSave}
          disabled={isLoading || isSaveDisabled}
          trackingId="save-topic-edit"
        >
          {isLoading ? 'Saving...' : 'Save Theme'}
        </Button>
      </div>
    </ContentDialog>
  );
};

export default TopicEditModal;
